import {
  useForm,
  Modal,
  TextField,
  PrimaryButton,
  NumberField,
  DropdownField,
  FormRow,
} from '@weave/design-system';
import { useCreateTagMutation } from 'apis/wetags/mutations';
import { listTagsQuery, namespacesListQuery } from 'apis/wetags/queriesV2';
import { useEffect } from 'react';
import { useQuery } from 'react-query';

export function CreateTagModal(props: {
  isOpen: boolean;
  onClose: () => void;
  namespaceId?: string;
}) {
  const { isOpen, onClose } = props;
  const { mutateAsync } = useCreateTagMutation();

  // TODO we're loading all namespaces here; if there are many namespaces, we should implement pagination or a search feature
  const { data = { namespaces: [] } } = useQuery(
    namespacesListQuery.getUseQueryOptions({
      pageSize: 500,
    })
  );

  // reset the form on modal close
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  const { formProps, getFieldProps, reset, values, isComplete } = useForm({
    fields: {
      namespaceId: {
        type: 'text',
        required: true,
        minLength: 1,
        maxLength: 255,
        value: props.namespaceId ?? getLastUsedNamespaceFromLS(),
        readOnly: !!props.namespaceId,
      },
      id: {
        type: 'text',
        required: true,
        minLength: 1,
        maxLength: 255,
        value: '',
        validateOnChange: true,
        validator: ({ value }) =>
          /^[0-9a-z-]+$/.test(value)
            ? ''
            : 'Tag ID can only contain lowercase letters, numbers, and hyphens.',
      },
      description: {
        type: 'text',
        required: true,
        minLength: 1,
        maxLength: 65535,
        value: '',
      },
      percentage: {
        type: 'number',
        required: false,
        value: '0',
      },
    },
    onSubmit: async (values) => {
      if (values.namespaceId && values.id) {
        try {
          setLastUsedNamespaceToLS(values.namespaceId);
          await mutateAsync({ tag: values as any });
          // Invalidate the tags list for the namespace to reflect the new tag
          listTagsQuery.invalidate({ namespaceId: values.namespaceId });
          reset();
          onClose();
        } catch (error) {}
      }
    },
  });

  return (
    <Modal show={isOpen} onClose={onClose}>
      <Modal.Header>Create New Tag</Modal.Header>
      <br />
      <Modal.Body>
        <form {...formProps} css={{ paddingTop: 5 }}>
          <FormRow>
            {props.namespaceId ? (
              <TextField {...getFieldProps('namespaceId')} label="Namespace" disabled />
            ) : (
              <DropdownField {...getFieldProps('namespaceId')} label="Namespace">
                {data.namespaces?.map((namespace) => (
                  <DropdownField.Option key={namespace.id} value={namespace.id ?? ''}>
                    {namespace.id}
                  </DropdownField.Option>
                ))}
              </DropdownField>
            )}
          </FormRow>
          <FormRow css={{ display: 'block' }}>
            <TextField
              {...getFieldProps('id')}
              label="Tag ID"
              placeholder="The name of the tag"
            />
          </FormRow>
          <FormRow>
            <TextField {...getFieldProps('description')} label="Description" />
          </FormRow>
          <FormRow>
            <NumberField {...getFieldProps('percentage')} label="Percentage" />
          </FormRow>
          <PrimaryButton type="submit" disabled={!isComplete}>
            Create
          </PrimaryButton>
        </form>
      </Modal.Body>
    </Modal>
  );
}

function getLastUsedNamespaceFromLS() {
  return localStorage.getItem('wetags.lastUsedNamespace') || '';
}

function setLastUsedNamespaceToLS(namespaceId: string) {
  localStorage.setItem('wetags.lastUsedNamespace', namespaceId);
}
